import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';

import { OutcomeReason, OutcomeTypeCode } from '../../../../../@types/sla.type';
import { Panel } from '../../../../../components/atoms/Panel';
import { SLAContactRowData, SLAOutcomeData } from '../../../../../components/organisms/SLAContactRow/SLAContactRow.props';
import { useGetAllOutcomeReasons } from '../../../../../queries/outcome-reason-query';
import { useCreateOutcomeReview } from '../../../../../queries/outcome-review-query';
import { useGetSLAsByServiceId, useRefreshSLAsByServiceId } from '../../../../../queries/sla-query';
import { SLADetailsPanelProps } from './SLADetailsPanel.props';

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const getStatusInfo = (sla: SLAContactRowData): { label: string; color: string; bgcolor: string } => {
  if (sla.isBreached) {
    const hasMitigation = sla.slaOutcomes?.some((o) => o.outcomeTypeCode === 'mitigated');
    const hasReview = sla.slaOutcomes?.some((o) => o.outcomeTypeCode === 'reviewed');
    if (hasMitigation) return { label: 'Mitigated', color: '#1565c0', bgcolor: '#e3f2fd' };
    if (hasReview) return { label: 'Reviewed', color: '#e65100', bgcolor: '#fff3e0' };
    return { label: 'Breached', color: '#c62828', bgcolor: '#ffebee' };
  }
  if (sla.isMet) return { label: 'Met', color: '#2e7d32', bgcolor: '#e8f5e9' };
  return { label: 'Running', color: '#546e7a', bgcolor: '#eceff1' };
};

const getBorderColor = (sla: SLAContactRowData): string => {
  if (sla.isBreached && (!sla.slaOutcomes || sla.slaOutcomes.length === 0)) return '#e53935';
  if (sla.isMet) return '#43a047';
  if (sla.isBreached) return '#fb8c00';
  return 'transparent';
};

// ── Outcome Form ────────────────────────────────────────────

type OutcomeTab = 'review' | 'mitigation';

const tabBtnSx = (active: boolean) => ({
  fontSize: '12px', fontWeight: active ? 600 : 400, fontFamily: 'inherit',
  px: 1.5, py: 0.5, cursor: 'pointer', border: 'none', borderRadius: '4px',
  bgcolor: active ? 'v4.accent' : 'transparent',
  color: active ? 'common.white' : 'text.secondary',
  '&:hover': active ? {} : { bgcolor: 'v4.tableHover' },
});

const OutcomeForm: React.FC<{
  sla: SLAContactRowData;
  reviewReasons: OutcomeReason[];
  mitigationReasons: OutcomeReason[];
  onSubmit: (tab: OutcomeTab, reasonId: number, notes: string) => void;
  onCancel: () => void;
  submitting: boolean;
}> = ({ sla, reviewReasons, mitigationReasons, onSubmit, onCancel, submitting }) => {
  const [tab, setTab] = useState<OutcomeTab>('review');
  const [reasonId, setReasonId] = useState<number>(0);
  const [notes, setNotes] = useState('');

  const reasons = tab === 'review' ? reviewReasons : mitigationReasons;
  const canSubmit = reasonId > 0 && !submitting;

  // Reset form when switching tabs
  useEffect(() => { setReasonId(0); setNotes(''); }, [tab]);

  return (
    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'v4.border' }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1 }}>
        {sla.scopeName}: {sla.configName}
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
        <Box component="button" sx={tabBtnSx(tab === 'review')} onClick={() => setTab('review')}>Review</Box>
        <Box component="button" sx={tabBtnSx(tab === 'mitigation')} onClick={() => setTab('mitigation')}>Mitigation</Box>
      </Box>

      <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.5 }}>
        {tab === 'review'
          ? 'Record why this SLA was breached'
          : 'Record why this breach is not attributable to us'}
      </Typography>

      <Select
        size="small" fullWidth displayEmpty
        value={reasonId || ''}
        onChange={(e) => setReasonId(Number(e.target.value))}
        sx={{ fontSize: '13px', mb: 1 }}
        inputProps={{ 'aria-label': `${tab === 'review' ? 'Review' : 'Mitigation'} reason` }}
      >
        <MenuItem value="" disabled sx={{ fontSize: '13px' }}>Select reason...</MenuItem>
        {reasons.map((r) => (
          <MenuItem key={r.id} value={r.id} sx={{ fontSize: '13px' }}>{r.name}</MenuItem>
        ))}
      </Select>

      <TextField
        size="small" fullWidth multiline minRows={2}
        placeholder="Notes (optional)..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        inputProps={{ 'aria-label': `${tab === 'review' ? 'Review' : 'Mitigation'} notes` }}
        sx={{ mb: 1.5, '& .MuiInputBase-input': { fontSize: '13px' } }}
      />

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel} sx={{ fontSize: '12px', textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          size="small" variant="contained" disabled={!canSubmit}
          onClick={() => onSubmit(tab, reasonId, notes)}
          sx={{ fontSize: '12px', textTransform: 'none', fontWeight: 600, bgcolor: 'v4.accent', '&:hover': { opacity: 0.9 } }}
        >
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

// ── SLA Row ─────────────────────────────────────────────────

const SLARow: React.FC<{
  sla: SLAContactRowData;
  expanded: boolean;
  onToggle: () => void;
}> = ({ sla, expanded, onToggle }) => {
  const status = getStatusInfo(sla);

  return (
    <Box
      sx={{
        p: 1.5, borderRadius: 1, borderLeft: 4,
        borderColor: getBorderColor(sla), bgcolor: 'v4.tableEven', mb: 1,
        cursor: sla.isBreached ? 'pointer' : 'default',
        '&:hover': sla.isBreached ? { opacity: 0.92 } : {},
      }}
      onClick={() => { if (sla.isBreached) onToggle(); }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Tooltip title={`Config ID: ${sla.configId}`} placement="top">
          <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
            {sla.scopeName}: {sla.configName}
          </Typography>
        </Tooltip>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {sla.isBreached && !expanded && (
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
              Click to review
            </Typography>
          )}
          <Box
            component="span"
            sx={{
              fontSize: '11px', fontWeight: 600, px: 1, py: '2px',
              borderRadius: '4px', color: status.color, bgcolor: status.bgcolor,
            }}
          >
            {status.label}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: sla.slaOutcomes?.length ? 0.75 : 0 }}>
        <Box>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Target</Typography>
          <Typography sx={{ fontSize: '12px' }}>{formatDateTime(sla.loggedSLADateTime)}</Typography>
        </Box>
        {sla.targetTypeName && (
          <Box>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Type</Typography>
            <Typography sx={{ fontSize: '12px' }}>{sla.targetTypeName}</Typography>
          </Box>
        )}
      </Box>

      {sla.slaOutcomes && sla.slaOutcomes.length > 0 && (
        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid', borderColor: 'v4.border' }}>
          {sla.slaOutcomes.map((outcome: SLAOutcomeData, idx: number) => (
            <Typography key={idx} sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.25 }}>
              {outcome.createdAt ? formatDateTime(outcome.createdAt) : ''}{' '}
              <Box component="span" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                {outcome.outcomeTypeCode}
              </Box>
              {outcome.reasonCategoryCode ? `: ${outcome.reasonCategoryCode}` : ''}
              {outcome.notes ? ` — ${outcome.notes}` : ''}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ── Panel ───────────────────────────────────────────────────

export const SLADetailsPanel: React.FC<SLADetailsPanelProps> = ({ serviceId }) => {
  const slaQuery = useGetSLAsByServiceId(serviceId);
  const refreshMutation = useRefreshSLAsByServiceId(serviceId);
  const outcomeReasonsQuery = useGetAllOutcomeReasons();
  const createOutcome = useCreateOutcomeReview(serviceId);

  const [expandedSlaId, setExpandedSlaId] = useState<number | null>(null);

  // Auto-refresh SLAs on mount so trading hours changes are picked up
  useEffect(() => {
    if (serviceId > 0) {
      refreshMutation.mutate(undefined, { onSuccess: () => slaQuery.refetch() });
    }
  }, [serviceId]);

  const slas = slaQuery.data ?? [];
  const allReasons = outcomeReasonsQuery.data ?? [];
  const reviewReasons = allReasons.filter((r) => r.outcomeTypeCode === OutcomeTypeCode.Reviewed);
  const mitigationReasons = allReasons.filter((r) => r.outcomeTypeCode === OutcomeTypeCode.Mitigated);

  const handleSubmit = async (slaId: number, tab: OutcomeTab, reasonId: number, notes: string) => {
    await createOutcome.mutateAsync({
      outcomeTypeCode: tab === 'review' ? OutcomeTypeCode.Reviewed : OutcomeTypeCode.Mitigated,
      outcomeReasonId: reasonId,
      description: '',
      notes,
      slaIds: [slaId],
    });
    setExpandedSlaId(null);
    slaQuery.refetch();
  };

  const refreshButton = (
    <Chip
      icon={<RefreshIcon sx={{ fontSize: 16 }} />}
      label="Refresh"
      size="small"
      onClick={() => refreshMutation.mutate(undefined)}
      sx={{ fontSize: '11px', cursor: 'pointer' }}
    />
  );

  return (
    <Panel title="SLA Details" actions={refreshButton}>
      {slaQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : slas.length === 0 ? (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          No SLAs configured for this service
        </Typography>
      ) : (
        <Box>
          {slas.map((sla) => (
            <React.Fragment key={sla.id}>
              <SLARow
                sla={sla}
                expanded={expandedSlaId === sla.id}
                onToggle={() => setExpandedSlaId(expandedSlaId === sla.id ? null : sla.id)}
              />
              {expandedSlaId === sla.id && sla.isBreached && (
                <OutcomeForm
                  sla={sla}
                  reviewReasons={reviewReasons}
                  mitigationReasons={mitigationReasons}
                  onSubmit={(tab, reasonId, notes) => handleSubmit(sla.id, tab, reasonId, notes)}
                  onCancel={() => setExpandedSlaId(null)}
                  submitting={createOutcome.isPending}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Panel>
  );
};
